/* eslint-disable no-unreachable-loop */
// React
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

// Libraries
import PropTypes from "prop-types";
import { Icon, Popup, Menu } from "semantic-ui-react";
import { isEmpty, includes, get, map } from "lodash";
import EmojiPicker from "emoji-picker-react";
import TextAreaAutoSize from "react-textarea-autosize";

// Redux
import { connect } from "react-redux";
import { sendMessage, sendEmailMessage } from "@Actions/chats";
import { clearDraft } from "@Actions/draft";

// Hooks
import useAudioRecording from "@Hooks/useAudioRecording";

// Utils
import { messageBuilder, autoCapitalize } from "@Utils/functionality";
import { getBlacklistParts } from "@Utils/formats";

// Context
import { useListRef } from "@Features/Monitor/Context";

// Components
import Text from "@Components/Text";
import VoiceNote from "../VoiceNote";

// Modals
import MapAddressSelector from "@Components/MapAddressSelector";
import PreviewAttachment from "../../Modals/PreviewAttachment";

// Styles
import styles from "./styles.module.css";

const ChatInputBar = ({
	dispatch,
	currentChat,
	isDisabled,
	userName,
	reply,
	setCommand,
	setCmdSearch,
	isWhatsappBlock = false,
	platformTypes,
	draft,
	blackListKeyWords,
	clearInputTrigger,
}) => {
	const [text, setText] = useState("");
	const [messageType, setMessageType] = useState("Text");
	const [files, setFiles] = useState(null);
	const [showEmojis, setShowEmojis] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [mapVisible, setMapVisible] = useState(false);
	const fileInput = useRef(null);
	const recorderControls = useAudioRecording();
	const { listRef } = useListRef();
	const textAreaRef = useRef(null);
	const highlightsRef = useRef(null);
	const [height, setHeight] = useState(64);

	const isEmail = get(currentChat, "channel.platform.name", "") === "Email";

	const handleScroll = (e) => {
		if (highlightsRef.current) {
			highlightsRef.current.scrollTop = e.target.scrollTop;
		}
	};

	const renderHighlightedText = () => {
		const parts = getBlacklistParts(text, blackListKeyWords);
		return parts.map((part, i) => {
			if (part.isBlocked) {
				return (
					<span key={`bl-${i}`} className={styles.blockedMark}>
						{part.text}
					</span>
				);
			}
			return <span key={`bl-${i}`}>{part.text}</span>;
		});
	};

	useLayoutEffect(() => {
		if (textAreaRef.current) {
			const newHeight = textAreaRef.current.offsetHeight + 16;
			if (newHeight >= 210) {
				setHeight(210);
			} else if (newHeight >= 64) {
				setHeight(newHeight);
			} else {
				setHeight(64);
			}
		}
	}, [text]);

	useEffect(() => {
		setText("");
		setMessageType("Text");
		setShowEmojis(false);
	}, [currentChat?.id]);

	useEffect(() => {
		if (!isEmpty(draft)) {
			setText(draft.text);
			setMessageType(draft.type);
			if (includes(["Image", "Audio", "Video", "Document"], draft.type)) {
				setFiles([draft.uri]);
				setPreviewVisible(true);
			}
		}
	}, [draft]);

	useEffect(() => {
		if (clearInputTrigger > 0) {
			setText("");
		}
	}, [clearInputTrigger]);

	const toggleEmojiPicker = () => {
		setShowEmojis((prev) => {
			return !prev;
		});
	};

	const openMap = () => {
		setMapVisible(true);
	};

	const closeMap = () => {
		setMapVisible(false);
	};

	const handleInput = (e) => {
		let { value } = e.target;

		// Autocorrector usando la función general
		value = autoCapitalize(value);

		setText(value);
	};

	const handleFileInput = async (e) => {
		let filesFromInput = [];
		if (isEmail) {
			for (const file of e.target.files) {
				const base64File = await new Promise((resolve) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						resolve({
							name: file.name,
							type: file.type,
							base64: reader.result,
						});
					};
					reader.readAsDataURL(file);
				});
				filesFromInput.push(base64File);
			}
		} else {
			filesFromInput = [...e.target.files];
		}
		setFiles(filesFromInput);
		fileInput.current.value = null;
		if (!isEmail) {
			setPreviewVisible(true);
		}
	};

	const handleEmojiInput = ({ emoji }) => {
		setText((prev) => {
			return prev + emoji;
		});
	};

	const handleAttachClick = (type) => {
		setMessageType(type);

		let acceptVal = "";
		switch (type) {
			case "Image":
				acceptVal = "image/*";
				fileInput.current.setAttribute("multiple", "multiple");
				break;
			case "Audio":
				acceptVal = "audio/*";
				fileInput.current.removeAttribute("multiple");
				break;
			case "Video":
				acceptVal = "video/*";
				fileInput.current.removeAttribute("multiple");
				break;
			case "Document":
				acceptVal = ".pdf, .doc, .docx, application/msword, application/pdf";
				fileInput.current.removeAttribute("multiple");
				break;
			default:
				acceptVal = "*/*";
				fileInput.current.setAttribute("multiple", "multiple");
				break;
		}

		fileInput.current.setAttribute("accept", acceptVal);
		fileInput.current.click();
	};

	const handleClosePreview = () => {
		setText("");
		setMessageType("Text");
		setFiles(null);
		setShowEmojis(false);
		setPreviewVisible(false);
		dispatch(clearDraft());
	};

	const handleSendMessage = async () => {
		if (!isEmpty(text)) {
			let result = false;
			if (text[0] === "/") {
				setCommand(text[1]);
				if (text.length > 3) {
					setCmdSearch(String(text).slice(3));
				}
				// We intentionally do not set result = true here,
				// so the user's input is not erased when the modal opens.
			} else if (isEmail) {
				const newMessage = messageBuilder({ chat: currentChat, reply, userName, text, type: "Email" });
				result = await dispatch(sendEmailMessage({ message: newMessage, attachments: files, listRef }));
			} else {
				const newMessage = messageBuilder({ chat: currentChat, reply, userName, text });
				result = await dispatch(sendMessage({ message: newMessage, listRef }));
			}
			if (result) {
				setText("");
				setMessageType("Text");
				setFiles(null);
				setShowEmojis(false);
				dispatch(clearDraft());
			}
		}
	};

	const handleSendLocation = (locationData) => {
		const { latitude, longitude } = locationData;
		const newMessage = messageBuilder({ chat: currentChat, reply, userName, type: "Location", uri: { latitude, longitude } });
		dispatch(sendMessage({ message: newMessage, listRef }));
		dispatch(clearDraft());
		setText("");
		setMessageType("Text");
		setFiles(null);
		setShowEmojis(false);
		setMapVisible(false);
	};

	const handleSendFileMessage = async (filesWithCaptions) => {
		for (const message of filesWithCaptions) {
			const { file, caption } = message;
			let result = false;

			if (typeof file === "string") {
				const newMessage = messageBuilder({
					chat: currentChat,
					type: messageType,
					reply,
					userName,
					text: caption,
					uri: file,
				});
				result = await dispatch(sendMessage({ message: newMessage, listRef }));
			} else {
				const mimeType = file.type;
				const tmp = file.name.split(".");
				const extension = tmp[tmp.length - 1];
				const url = URL.createObjectURL(file);
				const newFile = new Blob([file], { type: mimeType });
				newFile.lastModifiedDate = new Date();
				newFile.name = `${messageType}_${Date.now()}.${extension}`;
				const newMessage = messageBuilder({
					chat: currentChat,
					type: messageType,
					reply,
					userName,
					text: caption,
					uri: url,
				});
				result = await dispatch(sendMessage({ message: newMessage, file: newFile, listRef }));
			}
			if (result) {
				dispatch(clearDraft());
			} else {
				return false; // Stop processing further files or clearing if one fails
			}
		}

		setText("");
		setMessageType("Text");
		setFiles(null);
		setShowEmojis(false);
		return true;
	};

	const handleSendVoiceNote = (blob) => {
		const url = URL.createObjectURL(blob);
		const blobFile = new Blob([blob], { type: "audio/webm" });
		blobFile.lastModifiedDate = new Date();
		blobFile.name = `Audio_${Date.now()}.webm`;
		const newMessage = messageBuilder({ chat: currentChat, type: "Audio", reply, userName, text, uri: url });
		dispatch(sendMessage({ message: newMessage, file: blobFile, listRef }));
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const removeFile = (index) => {
		setFiles((prev) => {
			return prev.filter((_, i) => {
				return i !== index;
			});
		});
	};

	const handlePaste = (e) => {
		const items = e.clipboardData?.items;
		if (!items) return;

		for (const item of items) {
			let type = "Document";
			const filesFromPaste = [];
			if (item.type.startsWith("image/")) type = "Image";
			if (item.type.startsWith("audio/")) type = "Audio";
			if (item.type.startsWith("video/")) type = "Video";
			if (item.kind === "file") {
				const file = item.getAsFile();
				if (file) filesFromPaste.push(file);
			}

			if (filesFromPaste.length > 0) {
				e.preventDefault();
				setFiles(filesFromPaste);
				setMessageType(type);
				setPreviewVisible(true);
			}
			return;
		}
	};

	const renderFiles = () => {
		if (isEmpty(files)) return null;
		const rows = map(files, (item, index) => {
			return (
				<div className={styles.fileContainer} key={index}>
					<div className={styles.messageContainer}>
						<Text>{item.name}</Text>
					</div>
					<div
						className={styles.fileIconContainer}
						role="button"
						tabIndex="-1"
						onClick={() => {
							return removeFile(index);
						}}>
						<Icon name="times circle outline" size="large" />
					</div>
				</div>
			);
		});
		return rows;
	};

	if (isWhatsappBlock) {
		return (
			<div className={styles.centeredText} style={{ height }}>
				<Text>La conversación tiene más de 24 horas sin respuesta del cliente. Solo puedes enviar plantillas.</Text>
			</div>
		);
	}

	if (isEmpty(currentChat) || isDisabled) {
		return (
			<div className={styles.container} style={{ height }}>
				<span className={styles.disabledMessage}>
					{get(currentChat, "user.fullName", "") ? `Atendido por: ${get(currentChat, "user.fullName", "")}` : ""}
				</span>
			</div>
		);
	}

	return (
		<>
			{!isEmpty(files) && renderFiles()}
			<div className={styles.container} style={{ height }}>
				{showEmojis && (
					<div className={styles.picker}>
						<EmojiPicker
							onEmojiClick={handleEmojiInput}
							autoFocusSearch={false}
							previewConfig={{ showPreview: false }}
							style={{ zIndex: 5 }}
						/>
					</div>
				)}
				{previewVisible && (
					<PreviewAttachment
						attachment={files}
						type={messageType}
						text={text}
						handleDismiss={handleClosePreview}
						handleSend={handleSendFileMessage}
						platformTypes={platformTypes}
						blackListKeyWords={blackListKeyWords}
					/>
				)}
				{mapVisible && <MapAddressSelector onSelect={handleSendLocation} onClose={closeMap} />}
				<div
					className={showEmojis ? styles.focusedIconContainer : styles.buttonIconContainer}
					role="button"
					disabled={isDisabled}
					tabIndex="-1"
					onClick={toggleEmojiPicker}>
					<Icon name={showEmojis ? "close" : "smile outline"} size="large" />
				</div>
				{!isEmail && (
					<Popup
						className={styles.menu}
						on="click"
						position="top left"
						pinned
						basic
						trigger={
							<div className={styles.buttonIconContainer} role="button" tabIndex="-1">
								<Icon name="paperclip" size="large" />
							</div>
						}>
						<Popup.Content>
							<Menu vertical className={styles.menuVertical}>
								{includes(platformTypes, "Image") && (
									<Menu.Item
										className={styles.menuItem}
										disabled={isDisabled}
										onClick={() => {
											return handleAttachClick("Image", "image");
										}}>
										<Icon className={styles.menuIcon} name="images" />
										<Text className={styles.menuText} disabled={isDisabled}>
											Imagen
										</Text>
									</Menu.Item>
								)}
								{includes(platformTypes, "Video") && (
									<Menu.Item
										className={styles.menuItem}
										disabled={isDisabled}
										onClick={() => {
											return handleAttachClick("Video", "video");
										}}>
										<Icon className={styles.menuIcon} name="video" />
										<Text className={styles.menuText} disabled={isDisabled}>
											Video
										</Text>
									</Menu.Item>
								)}
								{includes(platformTypes, "Audio") && (
									<Menu.Item
										className={styles.menuItem}
										disabled={isDisabled}
										onClick={() => {
											return handleAttachClick("Audio", "audio");
										}}>
										<Icon className={styles.menuIcon} name="headphones" />
										<Text className={styles.menuText} disabled={isDisabled}>
											Audio
										</Text>
									</Menu.Item>
								)}
								{includes(platformTypes, "Document") && (
									<Menu.Item
										className={styles.menuItem}
										disabled={isDisabled}
										onClick={() => {
											return handleAttachClick("Document", "application");
										}}>
										<Icon className={styles.menuIcon} name="file" />
										<Text className={styles.menuText} disabled={isDisabled}>
											Documento
										</Text>
									</Menu.Item>
								)}
								{includes(platformTypes, "Location") && (
									<Menu.Item className={styles.menuItem} disabled={isDisabled} onClick={openMap}>
										<Icon className={styles.menuIcon} name="map" />
										<Text className={styles.menuText} disabled={isDisabled}>
											Location
										</Text>
									</Menu.Item>
								)}
							</Menu>
						</Popup.Content>
					</Popup>
				)}
				{isEmail && (
					<div className={styles.buttonIconContainer} role="button" tabIndex="-1" onClick={handleAttachClick}>
						<Icon name="paperclip" size="large" />
					</div>
				)}

				<input ref={fileInput} className={styles.fileInput} type="file" hidden onChange={handleFileInput} />
				<div className={styles.inputWrapper}>
					{!recorderControls.isRecording && (
						<>
							<div ref={highlightsRef} className={styles.highlights}>
								{renderHighlightedText()}
								{/* Handle trailing newline for visual consistency */}
								{text.endsWith("\n") && <br />}
							</div>
							<TextAreaAutoSize
								id="1"
								name="textarea"
								ref={textAreaRef}
								className={styles.textarea}
								disabled={isDisabled}
								placeholder="Escriba un mensaje"
								value={text}
								onChange={handleInput}
								onPaste={handlePaste}
								minRows={1}
								maxRows={10}
								onKeyDown={handleKeyDown}
								maxLength={4096}
								onScroll={handleScroll}
							/>
						</>
					)}
				</div>
				{isEmpty(text) && !isEmail && <VoiceNote onRecordingComplete={handleSendVoiceNote} recorderControls={recorderControls} />}
				{!isEmpty(text) && (
					<div className={styles.buttonIconContainer} disabled={isDisabled} role="button" tabIndex="-1" onClick={handleSendMessage}>
						<Icon name="send" size="large" />
					</div>
				)}
			</div>
		</>
	);
};

ChatInputBar.propTypes = {
	dispatch: PropTypes.func,
	currentChat: PropTypes.object,
	isDisabled: PropTypes.bool,
	userName: PropTypes.string,
	reply: PropTypes.object,
	setCommand: PropTypes.func,
	setCmdSearch: PropTypes.func,
	isWhatsappBlock: PropTypes.bool,
	platformTypes: PropTypes.array,
	draft: PropTypes.object,
	blackListKeyWords: PropTypes.array,
	clearInputTrigger: PropTypes.number,
};

function mapStateToProps({ user, chats, draft, company }) {
	return {
		userName: user.name,
		reply: chats.reply,
		draft: draft.draft,
		blackListKeyWords: company.settings.blackListKeyWords,
	};
}

export default connect(mapStateToProps)(ChatInputBar);
